import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || 'caibiao');

  const displayCategory = category || categories[0];
  const articles = useMemo(
    () => getArticlesByCategory(displayCategory.id),
    [displayCategory.id]
  );

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类浏览', path: '/category/caibiao' },
    { label: displayCategory.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayCategory.name} - 直播互动体验平台`,
    description: displayCategory.description,
    url: `https://streamhubx.github.io/category/${displayCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '直播互动体验平台',
      url: 'https://streamhubx.github.io/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.path ? `https://streamhubx.github.io${item.path}` : undefined,
    })),
  };

  return (
    <>
      <SEOHead
        title={`${displayCategory.name} - 直播互动体验平台`}
        description={displayCategory.description}
        keywords={`${displayCategory.name},直播互动,实时连线,视频直播,直播平台`}
        canonicalUrl={`https://streamhubx.github.io/category/${displayCategory.slug}`}
        ogTitle={`${displayCategory.name} - 直播互动体验平台`}
        ogDescription={displayCategory.description}
        ogUrl={`https://streamhubx.github.io/category/${displayCategory.slug}`}
        ogImage={displayCategory.image}
        schemaData={[categorySchema, breadcrumbSchema]}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <section className="w-full py-8 md:py-12 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className={`${displayCategory.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
                      {displayCategory.name}
                    </h1>
                    <p className="text-sm text-foreground-600 mt-1">
                      {displayCategory.description}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        cat.slug === displayCategory.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                {articles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {articles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-colors duration-300 cursor-pointer"
                      >
                        <div className="w-full h-48 overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            title={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-5">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {article.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 text-xs text-accent-700 bg-accent-100 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          <h2 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 font-serif">
                            {article.title}
                          </h2>
                          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-foreground-400">
                            <span>{article.author}</span>
                            <span>·</span>
                            <span>{article.publishDate}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                      <i className="ri-inbox-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-600">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}